import { useMemo } from 'react';
import styled from '@emotion/styled';

import { Link, useNavigate } from 'react-router-dom';

import { Grid, Card, CardHeader, CardContent, CardActions, IconButton, Button, Divider, lighten, darken, LinearProgress, useTheme } from '@mui/material';

import { CardBlockContainer, CardBlock, CardBlockHeader, CardSpacer } from '../../../utils/utilities/CardComponents';

import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import PaymentsIcon from '@mui/icons-material/Payments';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';

import MultiStatusChip from '../../../utils/utilities/MultiStatusChip';

import SimpleChip from '../../../utils/utilities/SimpleChip';

import { relativeTime, formatPhoneNumber, formatGenericDateTime, formatGenericDate, formatCurrency } from '../../../utils/util';

import { BasicFlex, LabelUpper } from '../../../utils/Common';

import { useQuery } from '@tanstack/react-query';

import { getInventoryAccounting } from '../apiService';

// use pageData context
import { usePageData } from '../../../contexts/PageData';

import { methods } from '../../Payments/helpers';

import StatBlock from '../../../utils/utilities/StatBlock';

import { startCase } from 'lodash';


const IconBox = styled(BasicFlex)`
  padding: 1rem;
  justify-content: center;
  width: fit-content;
  border-radius: 6px;
  background-color: ${props => lighten(props.$color || props.theme.colors.secondary, .85)};
  color: ${props => darken(props.$color || props.theme.colors.secondary, .6)};
`

const Title = styled.div`
  font-weight: 600;
  font-size: 1.6rem;
`

const Name = styled.div`
  /* font-weight: 600; */
  color: ${props => props.theme.colors.mildText};
  font-size: 1.4rem;
`

const BalanceInfo = styled(BasicFlex)`
  gap: 6rem;
`

const BalanceLabel = styled(LabelUpper)`
  font-weight: normal;
  font-size: 1.2rem;
  min-width: max-content;
`

const BalanceStat = styled(BasicFlex)`
  flex-direction: column;
  align-items: flex-start;
  gap: .5rem;
  min-width: max-content;
`

const BalanceAmount = styled.div`
  font-weight: 600;
  font-size: 1.8rem;
  color: ${props => props.$color || "inherit"};
  position: relative;
`

const PifDate = styled.div`
  /* color: ${props => props.theme.colors.mildText}; */
  font-size: 1.6rem;
  font-weight: 600;
`

const InvoiceTotal = styled.div`
  position: absolute;
  bottom: -1.8rem;
  left: 0;
  font-size: 1rem;
  color: ${props => props.theme.colors.mildText};
  background-color: ${props => props.theme.colors.softBg};
  padding: .1rem .5rem;
  border-radius: 4px;
  min-width: max-content;
  font-weight: 600;
`

const StyledPaymentLink = styled(Link)`
  display: block;
  text-decoration: none;
  width: 100%;
  color: inherit;
`

const PaymentLineStyles = styled(BasicFlex)`
  border-bottom: 1px solid ${props => props.theme.colors.border};
  padding: 1.5rem 0;

  &:last-of-type {
    border: none;
  }
`

const PaymentHeader = styled(CardBlockHeader)`
  margin: 1rem 0;
  margin-top: 3rem;
  letter-spacing: 1px;
`

const PaymentMethod = styled.div`
  font-weight: 600;
  font-size: 1.4rem;
`

const PaymentDate = styled.div`
  color: ${props => props.theme.colors.mildText};
  font-size: 1.2rem;
`

const PaymentAmount = styled.div`
  font-weight: 700;
`

const MethodIcon = styled(IconBox)`
  background-color: ${props => props.theme.colors.darkBg};
`

const Progress = styled.div`
  margin: 3.5rem 0;
`

const Mild = styled.span`
  color: ${props => props.theme.colors.mildText};
  font-size: 1.2rem;
`

const Totals = styled(BasicFlex)`
  padding: 1.6rem;
`

const StatLabel = styled.div`
  text-transform: uppercase;
  color: ${props => props.theme.colors.mildText};
  font-size: 1.2rem;
`

const StatAmount = styled.div`
  font-weight: 600;
  font-size: 1.8rem;
`

const Header = ({ Icon, title, name, color }) => {
  return (
    <BasicFlex gap="1.5rem" py="1.5rem" px="1.5rem" align="stretch">
      <IconBox $color={color}>
        <Icon />
      </IconBox>
      <BasicFlex dir="column" justify="space-between">
        <Title>{title}</Title>
        <Name>{name}</Name>
      </BasicFlex>
    </BasicFlex>
  )
}

const ProgressBar = ({ total, paid, color = "primary" }) => {
  let progress = ((paid / total) * 100)?.toFixed(2) || 0;
  return (
    <Progress>
      <BasicFlex>
        <div style={{fontSize: "1.2rem"}}><Mild>Paid</Mild> {formatCurrency(paid)}</div> <Mild>{progress}%</Mild>
      </BasicFlex>
      <LinearProgress variant="determinate" value={progress} sx={{marginTop: ".8rem", height: ".8rem", borderRadius: "4px"}} color={color} />
    </Progress>
  )
}

const PaymentLine = ({ payment }) => {
  const { amount, method, original_amount, date, created_at, payment_id, vendor_payment_id } = payment;

  let payMethod = methods.find((m) => m.value === method);

  let Icon = payMethod?.Icon;

  let isVendorPayment = !!vendor_payment_id;

  return (
    <StyledPaymentLink to={isVendorPayment ? `/vendor_payments/view/${vendor_payment_id}` : `/payments/view/${payment_id}`}>
      <PaymentLineStyles align="center" justify="space-between">
        <BasicFlex gap="1rem">
          <MethodIcon><Icon /></MethodIcon>
          <BasicFlex dir="column" gap=".6em">
            <PaymentMethod>{payMethod?.label}</PaymentMethod>
            <PaymentDate>{formatGenericDateTime(date)}</PaymentDate>
          </BasicFlex>
        </BasicFlex>

        <BasicFlex dir="column" gap=".4em">
          <PaymentAmount>{formatCurrency(amount)}</PaymentAmount>
          {original_amount > 0 && <SimpleChip label={<BasicFlex width="fit-content" gap=".5rem"> <NorthEastIcon sx={{fontSize: "1.2rem"}} /> of {formatCurrency(original_amount)} payment</BasicFlex>} style={{borderRadius: "6px", fontSize: "1.1rem", color: "#888"}} />}
        </BasicFlex>

      </PaymentLineStyles>
    </StyledPaymentLink>
  )

}

const Stat = ({ title, amount }) => {
  return (
    <BasicFlex dir="column" width="fit-content" gap=".5rem">
      <StatLabel>{title}</StatLabel>
      <StatAmount>{amount}</StatAmount>
    </BasicFlex>
  )
}

const StatusChip = ({ paid, total }) => {
  let text, color;

  if (paid >= total) { 
    text = "Paid"; color = "success";
  } else if (paid > 0) {
    text = "Partially Paid"; color = "warning";
  } else {
    text = "Not Paid"; color = "error";
  }

  return (
    <SimpleChip label={text} palette={color} />
  )
}

const AccountingTab = () => {
  const { data = {} } = usePageData();

  const { data: accounting } = useQuery({
    queryKey: ["inventory", "accounting", data?.inventory_id],
    queryFn: () => getInventoryAccounting(data?.inventory_id),
    enabled: !!data.inventory_id
  });

  let cb = useMemo(() => accounting?.customer_balance, [accounting]);
  let vb = useMemo(() => accounting?.vendor_balance, [accounting]);

  let vbPaidTotal = vb?.applied_payments?.reduce((acc, el) => acc + el?.amount, 0);

  let wigBillPaid = vbPaidTotal >= vb?.prorated_amount;

  let cbAmount = (cb?.calculated_amount - ((cb?.calculated_amount / cb?.total) * cb?.total_paid)) || 0;
  let vbAmount = (vb?.prorated_amount - vbPaidTotal) || 0;

  console.log(cbAmount, vbAmount, "cbAmount, vbAmount", vb?.balance);


  const theme = useTheme();

  return (
    <Grid container spacing={3}>

      <Grid size={{xs: 12, md: 6}}>
        <Card variant='outlined'>
          <Header Icon={PaymentsIcon} title="Customer" name={data?.customer_name} color={theme.colors.success} />
          <Divider />

          <CardContent>
            <BasicFlex align="flex-start">

              <BalanceInfo width="fit-content" align="flex-start">
                <BalanceStat>
                  <BalanceLabel>Wig total</BalanceLabel>
                  <BalanceAmount>{formatCurrency(cb?.calculated_amount)}
                    <InvoiceTotal>Invoice Total: {formatCurrency(cb?.total)}</InvoiceTotal></BalanceAmount>
                </BalanceStat>
                <BalanceStat>
                  <BalanceLabel>Balance Due</BalanceLabel>
                  <BalanceAmount $color={cbAmount > 0 ? theme.colors.error : theme.palette.text.hint}>{formatCurrency(cbAmount)}
                  </BalanceAmount>
                </BalanceStat>
                {cb?.balance <= 0 ? <BalanceStat>
                  <BalanceLabel>Paid in full Date</BalanceLabel>
                  <PifDate>{formatGenericDate(cb?.applied_payments?.[0]?.date, "short")}</PifDate>
                </BalanceStat> : null}
              </BalanceInfo>

              <StatusChip paid={cb?.total_paid} total={cb?.total} />

            </BasicFlex>

            <ProgressBar total={cb?.total} paid={cb?.total_paid} color="success" />

            <PaymentHeader>Payment History ({cb?.applied_payments?.length || 0})</PaymentHeader>

            <BasicFlex dir="column">
              {cb?.applied_payments?.map((p, i) => <PaymentLine key={i} payment={p} />)}
            </BasicFlex>
          </CardContent>

          <CardActions sx={{p: 2}}>
            <Link to={`/payments/edit`} state={{customer_id: data?.customer_id?.toString(), client_name: data?.customer_name,
            //  mobile_phone: customer.mobile_phone, email: customer.email

            }} style={{width: "100%"}} >
            <Button variant='outlined' fullWidth startIcon={<PaymentsRoundedIcon />} color="success">Add New Customer Payment</Button>
           </Link>
          </CardActions>

        </Card>
      </Grid>

      <Grid size={{xs: 12, md: 6}}>
        <Card variant='outlined'>
          <Header Icon={StorefrontOutlinedIcon} title="Vendor" name={data?.brand_name} />
          <Divider />

          <CardContent>
            <BasicFlex align="flex-start">

              <BalanceInfo width="fit-content" align="flex-start">
                <BalanceStat>
                  <BalanceLabel>Wig Cost</BalanceLabel>
                  <BalanceAmount>{formatCurrency(vb?.prorated_amount || 0)}</BalanceAmount>
                </BalanceStat>
                <BalanceStat>
                  <BalanceLabel>Balance Due</BalanceLabel>
                  <BalanceAmount $color={vbAmount > 0 ? theme.palette.error.main : theme.palette.text.hint}>{formatCurrency(vbAmount)}</BalanceAmount>
                </BalanceStat>
                {wigBillPaid ? <BalanceStat>
                  <BalanceLabel>Paid in full Date</BalanceLabel>
                  <PifDate>{formatGenericDate(vb?.applied_payments?.[0]?.date, "short")}</PifDate>
                </BalanceStat> : null}
              </BalanceInfo>

              <StatusChip paid={vbPaidTotal || 0} total={vb?.prorated_amount || 0} />

            </BasicFlex>

            <ProgressBar total={vb?.prorated_amount || 0} paid={vbPaidTotal || 0} color="secondary" />

            <PaymentHeader>Payments To Vendor ({vb?.applied_payments?.length || 0})</PaymentHeader>

            <BasicFlex dir="column">
              {vb?.applied_payments?.map((p, i) => <PaymentLine key={i} payment={p} />)}
            </BasicFlex>
          </CardContent>

          {/* <Divider /> */}

          <CardActions sx={{p: 2}}>
            <Link to={`/vendor_payments/edit`} state={{vendor_id: data?.vendor_id?.toString(), vendor_name: data?.brand_name,
            //  mobile_phone: customer.mobile_phone, email: customer.email
              inventory_id: data?.inventory_id, wig_id: data?.wig_id
            }} style={{width: "100%"}} >
            <Button variant='outlined' fullWidth startIcon={<PaymentsRoundedIcon />} color="secondary">Add New Vendor Payment</Button>
           </Link>
          </CardActions>

        </Card>
      </Grid>

      <Grid size={{xs: 12}}>

        <Card variant='outlined'>
          <Totals justify="flex-start" gap="1.5rem">
            <IconBox $color={theme.colors.info}>
              <TrendingUpRoundedIcon />
            </IconBox>
            <Stat title="Sale Price" amount={formatCurrency(cb?.calculated_amount || 0)} />
            <Divider orientation="vertical" flexItem />
            <Stat title="Cost" amount={formatCurrency(vb?.prorated_amount || 0)} />
            <Divider orientation="vertical" flexItem />
            <Stat title="Gross Profit" amount={formatCurrency((cb?.calculated_amount || 0) - (vb?.prorated_amount || 0))} />
            <Divider orientation="vertical" flexItem />
            <Stat title="Margin" amount={`${(((vb?.prorated_amount || 0) / (cb?.calculated_amount || 0)) * 100)?.toFixed(2)}%`} />
          </Totals>
        </Card>
        
      </Grid>

    </Grid>
  );

};

export default AccountingTab;